"use client";

import ForgotPasswordContent from "./ForgotPasswordContent";
import useOtpCountdown from "@/hooks/auth/useOtpCountdown";
import { FormProvider, useForm } from "react-hook-form";
import { parseAsStringEnum, useQueryState } from "nuqs";
import { useAuthModal } from "@/hooks/auth/useAuthModal";
import { useFormatPhone } from "@/hooks/useFormatPhone";
import { SendOtpParamSchema } from "@/types/auth";
import { useSendOtp } from "@/hooks/auth/useAuth";

type ForgotPasswordFormFields = {
  email: string;
  countryCode: string;
  phone: string;
};

const ForgotPasswordStep = () => {
  const {
    phone: globalPhone,
    setPhone,
    setEmail,
    setAuthMethod,
    setVerificationToken,
    setStep,
  } = useAuthModal();
  const { formatPhone } = useFormatPhone();
  const { startCountdown } = useOtpCountdown();
  const { mutateAsync: sendOtpAsync, isPending: isSendingOtp } = useSendOtp();

  const methods = useForm<ForgotPasswordFormFields>({
    defaultValues: {
      email: "",
      countryCode: "+855",
      phone: globalPhone ? formatPhone(globalPhone, "KH") : "",
    },
  });

  const [tab, setTab] = useQueryState(
    "tab",
    parseAsStringEnum(["email", "phone"]).withDefault("email"),
  );

  const activeTab = tab === "phone" ? "phone" : "email";
  const {
    handleSubmit,
    watch,
    setValue,
    setError,
    clearErrors,
    formState: { errors },
  } = methods;

  const emailValue = watch("email");
  const phoneValue = watch("phone");

  const handleTabChange = (nextTab: "email" | "phone") => {
    clearErrors();
    void setTab(nextTab);
  };

  const handlePhoneChange = (nextValue: string) => {
    clearErrors();
    setValue("phone", nextValue, {
      shouldDirty: true,
      shouldTouch: true,
    });
  };

  const onSubmit = async (data: ForgotPasswordFormFields) => {
    if (isSendingOtp) return;
    clearErrors();

    try {
      const type = activeTab;
      const targetPhone = data.phone?.replace(/\D/g, "");

      const payload = SendOtpParamSchema.parse({
        ...(type === "phone" ? { countryCode: "855" } : {}),
        ...(type === "phone"
          ? { phone: targetPhone }
          : { email: data.email?.trim() }),
        type: "forgot",
        method: type,
      });

      const response = await sendOtpAsync(payload);

      if (response.success) {
        const responseData = response.data as
          { verificationToken?: string } | undefined;
        if (
          responseData &&
          typeof responseData.verificationToken === "string"
        ) {
          await setVerificationToken(responseData.verificationToken);
        }

        if (type === "phone") {
          await setPhone(targetPhone);
        } else {
          await setEmail(data.email?.trim() || "");
        }
        await setAuthMethod(type);

        sessionStorage.setItem(
          "otp_resend_expiry",
          (Date.now() + 120 * 1000).toString(),
        );
        sessionStorage.setItem("auth_flow", "forgot");
        startCountdown();

        await setStep("otp");
      } else {
        const message = response.message || "Failed to send reset code";
        setError(activeTab, { type: "server", message });
        setError("root", { type: "server", message });
      }
    } catch {
      const message =
        activeTab === "phone"
          ? "Enter a valid phone number."
          : "Enter a valid email address.";
      setError(activeTab, { type: "validation", message });
      setError("root", { type: "validation", message });
    }
  };

  return (
    <FormProvider {...methods}>
      <ForgotPasswordContent
        activeTab={activeTab}
        phone={phoneValue}
        email={emailValue}
        isSubmitting={isSendingOtp}
        errorMessage={errors[activeTab]?.message || errors.root?.message}
        onTabChange={handleTabChange}
        onPhoneChange={handlePhoneChange}
        onSubmit={handleSubmit(onSubmit)}
      />
    </FormProvider>
  );
};

export default ForgotPasswordStep;
